import { fixedUi } from '@/i18n/fixedUi';

/**
 * TikTok 发布记录展示态（审核进程，不含上架 / 核价确认）。
 *
 * 主来源：平台 Get Product / Search Products 的 audit.status（同步更新回写本地）。
 * PRE_APPROVED / APPROVED → 已通过（上架与否看「产品信息」）。
 */

export const PUBLISH_RECORD_STATUS = {
  PENDING_REVIEW: '待审核',
  /** @deprecated 历史「待上架」读出时归一为「已上架」 */
  PENDING_LISTING: '待上架',
  /** @deprecated 发布记录不再区分核价确认；历史数据读出时归一为「待审核」 */
  PENDING_MERCHANT: '待商家确认',
  /** 审核通过但未必在架（PRE_APPROVED / APPROVED） */
  APPROVED: '已通过',
  /** Create Product + Activate / 平台 status=ACTIVATE：无审核周期直接上架 */
  LISTED: '已上架',
  REJECTED: '已驳回',
};

export const PUBLISH_RECORD_STATUS_TABS = [
  { id: 'all', get label() { return fixedUi('全部'); }, status: '' },
  { id: '待审核', get label() { return fixedUi('待审核'); }, status: '待审核' },
  { id: '已通过', get label() { return fixedUi('已通过'); }, status: '已通过' },
  { id: '已上架', get label() { return fixedUi('已上架'); }, status: '已上架' },
  { id: '已驳回', get label() { return fixedUi('已驳回'); }, status: '已驳回' },
];

/** 存储态仍为中文码；界面展示用 */
export function publishRecordStatusDisplayLabel(status) {
  const s = normalizePublishRecordStatusLabel(status);
  if (!s) return fixedUi(PUBLISH_RECORD_STATUS.PENDING_REVIEW);
  return fixedUi(s);
}

function isListedProductStatus(productStatus) {
  const status = String(productStatus || '').trim().toUpperCase();
  return status === 'ACTIVATE'
    || status === 'LIVE'
    || status === 'ACTIVE'
    || status === 'PUBLISHED';
}

/** 历史「待上架」→已上架；历史「待商家确认」→待审核。 */
export function normalizePublishRecordStatusLabel(status) {
  const s = String(status || '').trim();
  if (s === PUBLISH_RECORD_STATUS.PENDING_LISTING) {
    return PUBLISH_RECORD_STATUS.LISTED;
  }
  if (s === PUBLISH_RECORD_STATUS.PENDING_MERCHANT) {
    return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  }
  return s || PUBLISH_RECORD_STATUS.PENDING_REVIEW;
}

/**
 * 平台 audit.status + product.status → 发布记录文案。
 * TTS Create Product 无独立 SKC/SPU，以 product_id / status 为准；
 * 在架（ACTIVATE）记「已上架」，审核中记「待审核」。
 */
export function mapPlatformAuditToPublishLabel(auditStatus, productStatus) {
  const audit = String(auditStatus || '').trim().toUpperCase();
  const status = String(productStatus || '').trim().toUpperCase();

  if (status === 'FAILED' || audit === 'FAILED') return PUBLISH_RECORD_STATUS.REJECTED;
  // 已在架优先：无审核周期 / 审核通过后激活，均记已上架
  if (isListedProductStatus(status)) return PUBLISH_RECORD_STATUS.LISTED;
  if (audit === 'AUDITING') return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  if (audit === 'PRE_APPROVED' || audit === 'APPROVED') {
    return PUBLISH_RECORD_STATUS.APPROVED;
  }

  if (audit === 'NONE' || !audit) {
    if (status === 'PENDING') return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
    if (status === 'DRAFT') return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
    return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  }

  if (status === 'PENDING') return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
}

/** 从 Get Product 响应抽取 audit_failed_reasons 文案 */
export function extractAuditFailedRemark(productPayload) {
  const root = productPayload && typeof productPayload === 'object' ? productPayload : null;
  if (!root) return '';
  const reasons = root.audit_failed_reasons;
  if (!Array.isArray(reasons) || !reasons.length) return '';
  const parts = [];
  reasons.forEach((r) => {
    if (!r || typeof r !== 'object') return;
    const list = Array.isArray(r.reasons) ? r.reasons : (Array.isArray(r.reason) ? r.reason : []);
    list.forEach((x) => {
      const t = String(x || '').trim();
      if (t) parts.push(t);
    });
    const single = String(r.reason || r.message || r.position || '').trim();
    if (single && !parts.includes(single)) parts.push(single);
  });
  return parts.slice(0, 5).join('；');
}

/** 单条核价 orderStatus → 对发布记录的影响；无法判定时返回 null（不再产出「待商家确认」） */
export function reviewOrderStatusToPublishLabel(orderStatus) {
  const n = Number(orderStatus);
  if (!Number.isFinite(n)) return null;
  if (n === 0 || n === 1) return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  if (n === 2) return PUBLISH_RECORD_STATUS.APPROVED;
  if (n === 3 || n === 4) return PUBLISH_RECORD_STATUS.REJECTED;
  return null;
}

/**
 * @param {Array<string|number|null|undefined>} statusCodes 该记录/SKC 命中的核价 orderStatus 列表；空=核价单从未出现
 * @deprecated 列表主态已改平台 audit；保留供测试与兼容
 */
export function derivePublishStatusFromReviewStatuses(statusCodes = []) {
  const nums = (Array.isArray(statusCodes) ? statusCodes : [])
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  if (!nums.length) return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  if (nums.some((n) => n === 3 || n === 4)) return PUBLISH_RECORD_STATUS.REJECTED;
  if (nums.some((n) => n === 2) && nums.every((n) => n === 2)) return PUBLISH_RECORD_STATUS.APPROVED;
  if (nums.some((n) => n === 2)) return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
}

export function resolvePublishStatusTone(status) {
  const s = normalizePublishRecordStatusLabel(status);
  if (s === PUBLISH_RECORD_STATUS.APPROVED || s === PUBLISH_RECORD_STATUS.LISTED) return 'success';
  if (s === PUBLISH_RECORD_STATUS.REJECTED || s.includes('驳回')) return 'failed';
  return 'pending';
}

/** 从核价缓存行收集可用于匹配的 productSkuId */
export function collectReviewSkuIds(reviewRow) {
  const ids = new Set();
  const push = (v) => {
    const n = Number(v);
    if (Number.isSafeInteger(n) && n > 0) ids.add(String(n));
    const t = String(v ?? '').trim();
    if (t && t !== '0') ids.add(t);
  };
  if (!reviewRow || typeof reviewRow !== 'object') return ids;
  push(reviewRow.skuId);
  push(reviewRow.productSkuId);
  const list = reviewRow.productSkuIdList || reviewRow.productSkuIds;
  if (Array.isArray(list)) list.forEach(push);
  return ids;
}

/** 从发布记录 / SKC 行收集可匹配的 sku id */
export function collectRecordSkuIds(recordOrSkc) {
  const ids = new Set();
  const push = (v) => {
    const t = String(v ?? '').trim();
    if (t && t !== '0' && t !== 'null' && t !== 'undefined') ids.add(t);
  };
  if (!recordOrSkc || typeof recordOrSkc !== 'object') return ids;
  push(recordOrSkc.productSkuId);
  push(recordOrSkc.skuId);
  const list = recordOrSkc.productSkuIds || recordOrSkc.productSkuIdList;
  if (Array.isArray(list)) list.forEach(push);
  const merchants = recordOrSkc.merchantSkus;
  if (Array.isArray(merchants)) {
    merchants.forEach((m) => {
      push(m?.productSkuId);
      push(m?.skuId);
    });
  }
  const skcItems = recordOrSkc.skcItems;
  if (Array.isArray(skcItems)) {
    skcItems.forEach((skc) => {
      collectRecordSkuIds(skc).forEach((id) => ids.add(id));
    });
  }
  return ids;
}

/**
 * @param {Map<string, number>|Record<string, number>} skuStatusMap skuId → orderStatus
 * @param {Iterable<string>} skuIds
 */
export function lookupReviewStatusesForSkus(skuStatusMap, skuIds) {
  const out = [];
  const map = skuStatusMap instanceof Map
    ? skuStatusMap
    : new Map(Object.entries(skuStatusMap || {}));
  for (const id of skuIds || []) {
    const key = String(id);
    if (!map.has(key)) continue;
    out.push(map.get(key));
  }
  return out;
}

export function buildSkuReviewStatusMap(reviewItems = []) {
  const map = new Map();
  (Array.isArray(reviewItems) ? reviewItems : []).forEach((row) => {
    const status = Number(row?.orderStatus ?? row?.status);
    if (!Number.isFinite(status)) return;
    collectReviewSkuIds(row).forEach((id) => {
      const prev = map.get(id);
      if (prev == null) {
        map.set(id, status);
        return;
      }
      const rank = (n) => {
        if (n === 0 || n === 1) return 3;
        if (n === 3 || n === 4) return 2;
        if (n === 2) return 1;
        return 0;
      };
      if (rank(status) >= rank(prev)) map.set(id, status);
    });
  });
  return map;
}

/**
 * 发布记录不再叠加核价「待商家确认」；仅做历史状态归一。
 * @deprecated 保留函数名兼容旧调用
 */
export function applyMerchantConfirmOverlay(record, _skuStatusMap) {
  return normalizePublishRecordRow(record);
}

/** 读出时归一历史「待上架」「待商家确认」；在架 product.status 优先记「已上架」 */
export function normalizePublishRecordRow(record) {
  if (!record || typeof record !== 'object') return record;
  let label = normalizePublishRecordStatusLabel(record.statusLabel || record.status);
  if (isListedProductStatus(record.platformProductStatus)) {
    label = PUBLISH_RECORD_STATUS.LISTED;
  } else {
    const skcLabels = (Array.isArray(record.skcItems) ? record.skcItems : [])
      .map((skc) => normalizePublishRecordStatusLabel(skc?.statusLabel || skc?.status))
      .filter(Boolean);
    if (skcLabels.length && skcLabels.every((s) => s === PUBLISH_RECORD_STATUS.LISTED)) {
      label = PUBLISH_RECORD_STATUS.LISTED;
    }
  }
  const skcItems = Array.isArray(record.skcItems)
    ? record.skcItems.map((skc) => {
      if (!skc || typeof skc !== 'object') return skc;
      if (skc.status === label && skc.statusLabel === label) return skc;
      return { ...skc, status: label, statusLabel: label };
    })
    : record.skcItems;
  if (record.status === label
    && record.statusLabel === label
    && skcItems === record.skcItems) {
    return record;
  }
  return {
    ...record,
    status: label,
    statusLabel: label,
    skcItems,
  };
}

/** @deprecated 列表主态以平台 audit 为准；此处仅归一历史态 */
export function applyDerivedPublishStatuses(record, _skuStatusMap) {
  return normalizePublishRecordRow(record);
}
